import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Check, ArrowRight, Cpu, HardDrive, Server, Globe } from 'lucide-react';
import { useI18n } from '../context/I18nContext';
import { useCart } from '../context/CartContext';
import { useToast } from '../hooks/use-toast';
import { publicApi } from '../api';
import { useApi } from '../hooks/useApi';
import { parseServerFeatures, featureList, fmtPrice } from '../utils/media';

export default function PricingSection({ eyebrow, title, subtitle, ctaLabel, ctaHref, serverType, visibleCount = 3, showAll = false }) {
  const { lang, tr } = useI18n();
  const { add } = useCart();
  const { toast } = useToast();
  const nav = useNavigate();
  const [annual, setAnnual] = useState(false);
  const { data } = useApi(() => publicApi.servers(serverType, lang), [serverType, lang]);
  const items = useMemo(() => {
    const arr = (data || []).slice().sort((a, b) => (a.price_monthly || 0) - (b.price_monthly || 0));
    if (showAll) return arr;
    return arr.slice(0, visibleCount);
  }, [data, visibleCount, showAll]);

  if (items.length === 0) return null;

  // Feature index for highlighting mid
  const featuredIdx = items.length >= 3 ? Math.floor(items.length / 2) : (items.length >= 2 ? 1 : 0);

  return (
    <section className="relative py-16 md:py-24 lg:py-28">
      <div className="absolute inset-x-0 top-0 h-40 glow-radial pointer-events-none" />
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-10 relative">
        <div className="text-center max-w-3xl mx-auto mb-10 md:mb-12">
          <div className="text-xs font-bold tracking-[0.32em] text-[#e4b44e]/85 mb-3">{eyebrow}</div>
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-black text-white leading-tight">{title}</h2>
          <p className="text-white/60 mt-4 text-base md:text-lg">{subtitle}</p>
          <div className="inline-flex items-center gap-1 mt-8 p-1 rounded-full border border-white/10 bg-white/5">
            <button onClick={() => setAnnual(false)} className={`px-5 h-9 rounded-full text-xs font-bold tracking-wider transition ${!annual ? 'btn-gold' : 'text-white/70 hover:text-white'}`}>{tr('pricing.monthly', 'AYLIK')}</button>
            <button onClick={() => setAnnual(true)} className={`px-5 h-9 rounded-full text-xs font-bold tracking-wider transition ${annual ? 'btn-gold' : 'text-white/70 hover:text-white'}`}>{tr('pricing.yearly', 'YILLIK')} <span className="ml-1 text-[10px] text-emerald-400">-%17</span></button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {items.map((p, idx) => {
            const feats = parseServerFeatures(p.features);
            const isFeatured = idx === featuredIdx;
            const isHost = p.type === 'hosting';
            return (
              <div key={p.id} className={`relative rounded-2xl overflow-hidden border card-hover ${isFeatured ? 'border-[#e4b44e]/60 bg-gradient-to-b from-[#1a1710] to-[#0a090c] shadow-[0_20px_60px_-20px_rgba(228,180,78,0.4)]' : 'border-white/8 bg-gradient-to-b from-[#111015] to-[#0a090c]'}`}>
                {isFeatured && <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#e4b44e] to-transparent" />}
                {isFeatured && <div className="absolute top-4 right-4 px-2.5 py-1 text-[10px] font-black tracking-widest rounded bg-[#e4b44e]/15 border border-[#e4b44e]/40 text-[#f4d896]">{tr('pricing.popular', 'POPÜLER')}</div>}
                <div className="p-6 md:p-8">
                  <h3 className="font-display text-2xl font-bold text-white mb-1">{p.title}</h3>
                  <p className="text-sm text-white/55 mb-6">{isHost ? tr('pricing.hosting_tagline', 'Hızlı ve güvenli web hosting.') : p.type === 'server' ? tr('pricing.vps_tagline', 'Yüksek performanslı sanal sunucu.') : tr('pricing.vds_tagline', 'Fiziksel/izole sunucu.')}</p>
                  <div className="flex items-baseline gap-1 mb-6">
                    <span className="text-white/60 text-lg">₺</span>
                    <span className="font-display text-4xl md:text-5xl font-black gold-text">{fmtPrice(annual ? p.price_yearly : p.price_monthly)}</span>
                    <span className="text-white/50 text-sm ml-1">/ {annual ? tr('pricing.year', 'yıl') : tr('pricing.month', 'ay')}</span>
                  </div>
                  {isHost && (
                    <div className="space-y-2 mb-6 pb-6 border-b border-white/8">
                      <div className="flex items-center gap-2 text-sm text-white/80"><Server className="w-4 h-4 text-[#e4b44e]" /> LiteSpeed</div>
                      {feats.disk && <div className="flex items-center gap-2 text-sm text-white/80"><HardDrive className="w-4 h-4 text-[#e4b44e]" /> {feats.disk}</div>}
                    </div>
                  )}
                  {!isHost && (
                    <div className="space-y-2 mb-6 pb-6 border-b border-white/8">
                      {feats.cpu && <div className="flex items-center gap-2 text-sm text-white/80"><Cpu className="w-4 h-4 text-[#e4b44e]" /> {feats.cpu}</div>}
                      {feats.ram && <div className="flex items-center gap-2 text-sm text-white/80"><HardDrive className="w-4 h-4 text-[#e4b44e]" /> {feats.ram}</div>}
                      {feats.disk && <div className="flex items-center gap-2 text-sm text-white/80"><HardDrive className="w-4 h-4 text-[#e4b44e]" /> {feats.disk}</div>}
                    </div>
                  )}
                  <ul className="space-y-2.5 mb-8">
                    {featureList(feats).map((f, i) => (<li key={i} className="flex items-start gap-2 text-sm text-white/75"><Check className="w-4 h-4 text-[#e4b44e] mt-0.5 shrink-0" /> {f}</li>))}
                  </ul>
                  <button onClick={() => {
                    const price = annual ? p.price_yearly : p.price_monthly;
                    const freqText = annual ? tr('pricing.yearly_short', 'Yıllık') : tr('pricing.monthly_short', 'Aylık');
                    add({ id: `srv-${p.id}-${annual ? 'y' : 'm'}`, slug: p.slug || `srv-${p.id}`, name: `${p.title} (${freqText})`, price, image: null }, 1);
                    toast({ title: tr('cart.added', 'Sepete eklendi ✓'), description: `${p.title} — ${freqText}` });
                    nav('/cart');
                  }} className={`w-full h-12 rounded-full inline-flex items-center justify-center gap-2 text-sm font-extrabold tracking-widest ${isFeatured ? 'btn-gold' : 'btn-outline-gold'}`}>{tr('pricing.buy', 'SATIN AL')} <ArrowRight className="w-4 h-4" /></button>
                </div>
              </div>
            );
          })}
        </div>

        {ctaHref && !showAll && (
          <div className="text-center mt-10">
            <Link to={ctaHref} className="text-sm font-semibold tracking-widest text-[#f4d896] hover:text-white transition inline-flex items-center gap-2">
              <Globe className="w-4 h-4" /> {ctaLabel} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
